import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS } from "../i18n";
import { resolvePath, pathFor, langOfSlugPath } from "../config/routeSlugs";
import { X } from "lucide-react";

/**
 * Sprint 132 — L'URL determina l'idioma (opció B)
 * ==================================================================
 * Dues responsabilitats, deliberadament juntes perquè comparteixen la
 * mateixa lectura de la ruta:
 *
 * 1. LanguageSync — manté i18n.language alineat amb la URL a CADA
 *    navegació. El detector de i18n/index.js només corre a l'arrencada;
 *    dins la SPA els canvis de ruta no el tornen a disparar.
 *
 *    LangRoute ja fa això per a les rutes prefixades (/{lang}/...), però
 *    no cobreix el camí invers: si estàs a /en/blog/x i cliques un enllaç
 *    a /calculadoras (canònica castellana), sense això et quedaries en
 *    anglès a una URL espanyola — el mateix desajust que volem eliminar.
 *
 * 2. El banner de suggeriment. Si l'idioma del navegador no coincideix
 *    amb el de la URL i tenim aquesta ruta en el seu idioma, li oferim
 *    l'enllaç. MAI redirigim automàticament: les redireccions per
 *    Accept-Language impedeixen que Google indexi les altres versions
 *    (només veuria la que li toca pel seu header).
 */

const LANG_NAMES = {
  es: "Español", en: "English", fr: "Français",
  de: "Deutsch", it: "Italiano", nl: "Nederlands",
};

const BANNER_COPY = {
  es: { text: "Esta página también está en español", cta: "Ver en español", dismiss: "Cerrar" },
  en: { text: "This page is also available in English", cta: "View in English", dismiss: "Dismiss" },
  fr: { text: "Cette page existe aussi en français", cta: "Voir en français", dismiss: "Fermer" },
  de: { text: "Diese Seite gibt es auch auf Deutsch", cta: "Auf Deutsch ansehen", dismiss: "Schließen" },
  it: { text: "Questa pagina è disponibile anche in italiano", cta: "Vedi in italiano", dismiss: "Chiudi" },
  nl: { text: "Deze pagina is ook in het Nederlands beschikbaar", cta: "Bekijk in het Nederlands", dismiss: "Sluiten" },
};

const DISMISS_KEY = "poollex_lang_suggestion_dismissed";

/**
 * Idioma que declara la URL. Sense prefix = ES (canònica), llevat de les
 * seccions amb slug per idioma on la ruta sense prefix és d'un altre idioma
 * (Sprint 161: /tools/pool-volume-calculator és l'anglesa). Mateixa regla que
 * el detector de i18n/index.js: si no coincidissin, LangRoute i aquest
 * component es tornarien l'idioma l'un a l'altre en bucle.
 */
export function langFromPath(pathname) {
  const seg = (pathname.split("/")[1] || "").toLowerCase();
  if (SUPPORTED_LANGS.includes(seg)) return seg;
  return langOfSlugPath(pathname) || "es";
}

/** Idioma preferit del navegador, normalitzat i validat. */
function browserLang() {
  if (typeof navigator === "undefined") return null;
  const raw = navigator.languages?.[0] || navigator.language || "";
  const code = raw.split("-")[0].toLowerCase();
  return SUPPORTED_LANGS.includes(code) ? code : null;
}

const LanguageSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [suggestion, setSuggestion] = useState(null);

  const urlLang = langFromPath(location.pathname);

  // ── 1. Sincronitza i18n amb la URL a cada navegació ──
  useEffect(() => {
    if (i18n.language !== urlLang) {
      i18n.changeLanguage(urlLang);
    }
  }, [urlLang, i18n]);

  // ── 2. Suggeriment (no redirecció) ──
  useEffect(() => {
    setSuggestion(null);
    const pref = browserLang();
    if (!pref || pref === urlLang) return;

    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
    } catch (e) {
      // Storage bloquejat (mode privat, cookies desactivades): sense banner.
      return;
    }

    // Construeix la ruta equivalent. Només oferim el salt si sabem que
    // la ruta existeix en l'altre idioma: les canòniques ES sense prefix
    // que estan mapejades a App.js amb LangRoute.
    // Sprint 161 — Les seccions amb slug per idioma no es tradueixen posant
    // prefix: /en/calculadora-volumen-piscina no existeix. Se'n demana la
    // ruta a routeSlugs, i si no hi ha versió en aquell idioma, cap banner.
    const hit = resolvePath(location.pathname);
    let target;
    if (hit) {
      target = pathFor(hit.key, pref);
    } else {
      const tail = SUPPORTED_LANGS.includes((location.pathname.split("/")[1] || "").toLowerCase())
        ? "/" + location.pathname.split("/").slice(2).join("/")
        : location.pathname;
      target = pref === "es" ? tail : `/${pref}${tail === "/" ? "" : tail}`;
    }
    if (!target || target === location.pathname) return;

    setSuggestion({ lang: pref, target });
  }, [location.pathname, urlLang]);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch (e) { /* noop */ }
    setSuggestion(null);
  };

  if (!suggestion) return null;
  const copy = BANNER_COPY[suggestion.lang] || BANNER_COPY.en;

  return (
    <div
      className="bg-slate-900 text-white text-sm"
      role="region"
      aria-label={`Language suggestion: ${LANG_NAMES[suggestion.lang]}`}
      data-testid="lang-suggestion-banner"
    >
      <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <span className="text-slate-200">{copy.text}</span>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => { dismiss(); navigate(suggestion.target); }}
            className="px-3 py-1 bg-white text-slate-900 font-medium hover:bg-slate-100 transition-colors"
            data-testid="lang-suggestion-accept"
          >
            {copy.cta}
          </button>
          <button
            onClick={dismiss}
            className="p-1 text-slate-400 hover:text-white transition-colors"
            aria-label={copy.dismiss}
            data-testid="lang-suggestion-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSync;
